import {
  TIER_IDS,
  FEATURE_KEYS,
  WORKSPACE_COUNT_LIMIT_KEYS,
  resolveTierLimit,
  resolveTeamWorkspaceCaps,
  resolveTeamWorkspaceDefaults,
  type PlanLimitOverrides,
  type TierId,
  type FeatureKey,
  type WorkspaceCountLimitKey,
  type TierLimitOverrides,
} from '../../../config/planLimitsCore'
import { WORKSPACE_LIMIT_HARD_CAPS } from '../../../config/workspaceLimitCaps'

export type MatrixCell = {
  effectiveValue: number
  codeDefault: number
  ceiling: number | null
  isOverridden: boolean
}

export type MatrixRow = {
  section: 'tierLimits' | 'workspaceDefaults' | 'workspaceCaps'
  key: FeatureKey | WorkspaceCountLimitKey
  cells: Record<TierId, MatrixCell>
}

export type PlanLimitsMatrix = {
  rows: MatrixRow[]
}

export type TierDraft = {
  tierLimits?: Partial<Record<FeatureKey, number | string | 'default' | null | undefined>>
  workspaceDefaults?: Partial<Record<WorkspaceCountLimitKey, number | string | 'default' | null | undefined>>
  workspaceCaps?: Partial<Record<WorkspaceCountLimitKey, number | string | 'default' | null | undefined>>
}

export function buildMatrix(overrides?: PlanLimitOverrides): PlanLimitsMatrix {
  const rows: MatrixRow[] = []

  // 1. Tier limits (14 rows)
  for (const featKey of FEATURE_KEYS) {
    const cells = {} as Record<TierId, MatrixCell>
    for (const tier of TIER_IDS) {
      const codeDefault = resolveTierLimit(tier, featKey)
      const effectiveValue = resolveTierLimit(tier, featKey, overrides)
      const isOverridden = overrides?.tiers?.[tier]?.tierLimits?.[featKey] !== undefined
      cells[tier] = {
        effectiveValue,
        codeDefault,
        ceiling: null,
        isOverridden,
      }
    }
    rows.push({
      section: 'tierLimits',
      key: featKey,
      cells,
    })
  }

  // 2. Team workspace defaults (11 rows)
  for (const capKey of WORKSPACE_COUNT_LIMIT_KEYS) {
    const cells = {} as Record<TierId, MatrixCell>
    for (const tier of TIER_IDS) {
      const codeDefaults = resolveTeamWorkspaceDefaults(tier, undefined)
      const codeDefault = codeDefaults[capKey]
      const effectiveDefaults = resolveTeamWorkspaceDefaults(tier, overrides)
      const effectiveValue = effectiveDefaults[capKey]
      const effectiveCaps = resolveTeamWorkspaceCaps(tier, overrides)
      const ceiling = effectiveCaps[capKey]
      const isOverridden = overrides?.tiers?.[tier]?.workspaceDefaults?.[capKey] !== undefined
      cells[tier] = {
        effectiveValue,
        codeDefault,
        ceiling,
        isOverridden,
      }
    }
    rows.push({
      section: 'workspaceDefaults',
      key: capKey,
      cells,
    })
  }

  // 3. Team workspace caps (11 rows)
  for (const capKey of WORKSPACE_COUNT_LIMIT_KEYS) {
    const cells = {} as Record<TierId, MatrixCell>
    for (const tier of TIER_IDS) {
      const codeCaps = resolveTeamWorkspaceCaps(tier, undefined)
      const codeDefault = codeCaps[capKey]
      const effectiveCaps = resolveTeamWorkspaceCaps(tier, overrides)
      const effectiveValue = effectiveCaps[capKey]
      const ceiling = WORKSPACE_LIMIT_HARD_CAPS[capKey]
      const isOverridden = overrides?.tiers?.[tier]?.workspaceCaps?.[capKey] !== undefined
      cells[tier] = {
        effectiveValue,
        codeDefault,
        ceiling,
        isOverridden,
      }
    }
    rows.push({
      section: 'workspaceCaps',
      key: capKey,
      cells,
    })
  }

  return { rows }
}

export function buildTierPayload(
  current: PlanLimitOverrides | null | undefined,
  tier: TierId,
  tierDraft: TierDraft,
): PlanLimitOverrides {
  const version = current?.version ?? 0
  const currentTiers = current?.tiers ?? {}

  const newTiers: Partial<Record<TierId, TierLimitOverrides>> = {}
  for (const t of TIER_IDS) {
    if (t !== tier && currentTiers[t]) {
      newTiers[t] = {
        ...(currentTiers[t]?.tierLimits ? { tierLimits: { ...currentTiers[t]?.tierLimits } } : {}),
        ...(currentTiers[t]?.workspaceCaps ? { workspaceCaps: { ...currentTiers[t]?.workspaceCaps } } : {}),
        ...(currentTiers[t]?.workspaceDefaults ? { workspaceDefaults: { ...currentTiers[t]?.workspaceDefaults } } : {}),
      }
    }
  }

  const parseVal = (val: unknown) => {
    if (val === undefined || val === null || val === 'default') return undefined
    if (typeof val === 'number') return val
    if (typeof val === 'string') {
      const trimmed = val.trim()
      if (trimmed === '') return undefined
      const num = Number(trimmed)
      return Number.isNaN(num) ? trimmed : num
    }
    return val
  }

  const updatedTier: TierLimitOverrides = {}

  // 1. tierLimits
  if (tierDraft.tierLimits) {
    const cleanLimits: Partial<Record<FeatureKey, number>> = {}
    for (const key of FEATURE_KEYS) {
      const parsed = parseVal(tierDraft.tierLimits[key])
      if (parsed !== undefined) {
        cleanLimits[key] = parsed as number
      }
    }
    if (Object.keys(cleanLimits).length > 0) {
      updatedTier.tierLimits = cleanLimits
    }
  }

  // 2. workspaceCaps
  if (tierDraft.workspaceCaps) {
    const cleanCaps: Partial<Record<WorkspaceCountLimitKey, number>> = {}
    for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
      const parsed = parseVal(tierDraft.workspaceCaps[key])
      if (parsed !== undefined) {
        cleanCaps[key] = parsed as number
      }
    }
    if (Object.keys(cleanCaps).length > 0) {
      updatedTier.workspaceCaps = cleanCaps
    }
  }

  // 3. workspaceDefaults
  if (tierDraft.workspaceDefaults) {
    const cleanDefaults: Partial<Record<WorkspaceCountLimitKey, number>> = {}
    for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
      const parsed = parseVal(tierDraft.workspaceDefaults[key])
      if (parsed !== undefined) {
        cleanDefaults[key] = parsed as number
      }
    }
    if (Object.keys(cleanDefaults).length > 0) {
      updatedTier.workspaceDefaults = cleanDefaults
    }
  }

  // Only attach updatedTier if it has at least one override
  if (updatedTier.tierLimits || updatedTier.workspaceCaps || updatedTier.workspaceDefaults) {
    newTiers[tier] = updatedTier
  }

  return {
    version,
    tiers: newTiers,
  }
}
